import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight, MapPin, User, Briefcase, Loader2, Star } from "lucide-react";

const WD = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];
const MONTHS = ["Gennaio","Febbraio","Marzo","Aprile","Maggio","Giugno",
                "Luglio","Agosto","Settembre","Ottobre","Novembre","Dicembre"];

const CAT_COLORS = {
  elettricista: { bg: "bg-amber-100", text: "text-amber-900", dot: "bg-amber-500" },
  climatizzazione: { bg: "bg-sky-100", text: "text-sky-900", dot: "bg-sky-500" },
  idraulico: { bg: "bg-blue-100", text: "text-blue-900", dot: "bg-blue-500" },
  fotovoltaico: { bg: "bg-emerald-100", text: "text-emerald-900", dot: "bg-emerald-500" },
  sicurezza: { bg: "bg-red-100", text: "text-red-900", dot: "bg-red-500" },
  automazione: { bg: "bg-purple-100", text: "text-purple-900", dot: "bg-purple-500" },
  antincendio: { bg: "bg-rose-100", text: "text-rose-900", dot: "bg-rose-500" },
};
const catColor = (c) => CAT_COLORS[(c || "").toLowerCase()] || { bg: "bg-slate-100", text: "text-slate-800", dot: "bg-slate-400" };

const STATUS_LABEL = { da_fare: "Da fare", in_corso: "In corso", pausa: "In pausa", chiuso: "Chiuso" };

function toISO(d) { const y = d.getFullYear(); const m = String(d.getMonth()+1).padStart(2,"0"); const dd = String(d.getDate()).padStart(2,"0"); return `${y}-${m}-${dd}`; }
function parseISO(s) { if (!s) return null; const [y,m,d] = s.split("-").map(Number); return new Date(y, (m||1)-1, d||1); }
function sameDay(a, b) { return a && b && a.getFullYear()===b.getFullYear() && a.getMonth()===b.getMonth() && a.getDate()===b.getDate(); }

export default function Calendar() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [cursor, setCursor] = useState(new Date()); // first day of displayed month
  const [jobs, setJobs] = useState([]);
  const [busy, setBusy] = useState(false);
  const [selectedDay, setSelectedDay] = useState(toISO(new Date()));
  const [view, setView] = useState("month"); // "month" | "day"

  const isOperaio = user?.role === "operaio";
  const isStaff = user?.role === "personale" || user?.role === "admin";

  useEffect(() => {
    if (!loading && !user) navigate("/login");
    else if (!loading && user?.role === "cliente") navigate("/dashboard");
  }, [user, loading, navigate]);

  const monthStart = useMemo(() => new Date(cursor.getFullYear(), cursor.getMonth(), 1), [cursor]);
  const monthEnd = useMemo(() => new Date(cursor.getFullYear(), cursor.getMonth()+1, 0), [cursor]);

  // Build the grid: 42 cells starting on Monday
  const gridDays = useMemo(() => {
    const first = new Date(monthStart);
    const dow = (first.getDay() + 6) % 7; // 0=Mon ... 6=Sun
    first.setDate(first.getDate() - dow);
    const out = [];
    for (let i=0; i<42; i++) {
      const d = new Date(first); d.setDate(first.getDate() + i); out.push(d);
    }
    return out;
  }, [monthStart]);

  useEffect(() => {
    (async () => {
      if (!user) return;
      setBusy(true);
      try {
        const start = toISO(gridDays[0]);
        const end = toISO(gridDays[gridDays.length - 1]);
        const r = await api.get(`/jobs/calendar?start=${start}&end=${end}`);
        setJobs(r.data || []);
      } catch (e) { toast.error("Impossibile caricare il calendario"); } finally { setBusy(false); }
    })();
  }, [gridDays, user]);

  // Group jobs by date
  const jobsByDay = useMemo(() => {
    const m = {};
    for (const j of jobs) {
      const k = j.scheduled_date; if (!k) continue;
      (m[k] ||= []).push(j);
    }
    // Sort each day: operaio's jobs first (mine), then by status/city
    const sortFn = (a, b) => {
      if (isOperaio) {
        const mineA = (a.assigned_operai || []).includes(user.user_id);
        const mineB = (b.assigned_operai || []).includes(user.user_id);
        if (mineA !== mineB) return mineA ? -1 : 1;
      }
      return (a.city || "").localeCompare(b.city || "");
    };
    Object.values(m).forEach(arr => arr.sort(sortFn));
    return m;
  }, [jobs, isOperaio, user]);

  const isMine = (j) => isOperaio && (j.assigned_operai || []).includes(user?.user_id);

  const prevMonth = () => setCursor(new Date(cursor.getFullYear(), cursor.getMonth()-1, 1));
  const nextMonth = () => setCursor(new Date(cursor.getFullYear(), cursor.getMonth()+1, 1));
  const goToday = () => { const t = new Date(); setCursor(new Date(t.getFullYear(), t.getMonth(), 1)); setSelectedDay(toISO(t)); };

  const today = new Date();
  const dayJobs = jobsByDay[selectedDay] || [];

  if (loading || !user) return <div className="min-h-screen"><Navbar/></div>;

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar/>
      <div className="max-w-7xl mx-auto p-5">
        <div className="mt-6 mb-5 flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="text-xs uppercase tracking-widest text-blue-800 font-semibold">Pianificazione</div>
            <h1 className="heading text-3xl md:text-4xl font-extrabold text-slate-900 flex items-center gap-2">
              <CalendarIcon className="w-7 h-7"/> Calendario lavori
            </h1>
            <p className="text-sm text-slate-600 mt-1">
              {isOperaio
                ? "I tuoi lavori sono evidenziati in verde in cima a ogni giornata. In grigio quelli assegnati ai colleghi."
                : "Pianificazione completa dei lavori. Clicca un giorno per vedere i dettagli."}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <div className="hidden md:flex items-center gap-1 rounded-md border border-slate-200 bg-white p-0.5">
              <button onClick={() => setView("month")}
                      className={`text-xs px-2.5 py-1 rounded ${view==="month" ? "bg-blue-800 text-white" : "text-slate-600 hover:bg-slate-100"}`}
                      data-testid="cal-view-month">Mese</button>
              <button onClick={() => setView("day")}
                      className={`text-xs px-2.5 py-1 rounded ${view==="day" ? "bg-blue-800 text-white" : "text-slate-600 hover:bg-slate-100"}`}
                      data-testid="cal-view-day">Giorno</button>
            </div>
            <Button variant="outline" onClick={prevMonth} data-testid="cal-prev"><ChevronLeft className="w-4 h-4"/></Button>
            <Button variant="outline" onClick={goToday} data-testid="cal-today">Oggi</Button>
            <Button variant="outline" onClick={nextMonth} data-testid="cal-next"><ChevronRight className="w-4 h-4"/></Button>
          </div>
        </div>

        <div className="mb-4 text-center">
          <h2 className="heading text-2xl font-bold text-slate-900 capitalize" data-testid="cal-title">
            {MONTHS[cursor.getMonth()]} {cursor.getFullYear()}
          </h2>
        </div>

        {view === "month" ? (
          <Card className="p-2 overflow-hidden">
            <div className="grid grid-cols-7 gap-px bg-slate-200 rounded-md overflow-hidden">
              {WD.map(w => (
                <div key={w} className="bg-slate-50 text-center text-xs font-semibold uppercase tracking-widest text-slate-500 py-2">{w}</div>
              ))}
              {gridDays.map((d, idx) => {
                const iso = toISO(d);
                const inMonth = d.getMonth() === cursor.getMonth();
                const isToday = sameDay(d, today);
                const isSel = iso === selectedDay;
                const list = jobsByDay[iso] || [];
                const mineCount = isOperaio ? list.filter(isMine).length : 0;
                return (
                  <button key={idx} onClick={() => setSelectedDay(iso)}
                          className={`relative bg-white min-h-[96px] text-left p-1.5 flex flex-col hover:bg-blue-50 transition ${inMonth ? "" : "opacity-40"} ${isSel ? "ring-2 ring-inset ring-blue-500" : ""}`}
                          data-testid={`cal-day-${iso}`}>
                    <div className="flex items-center justify-between">
                      <span className={`inline-flex items-center justify-center text-xs font-semibold w-6 h-6 rounded-full ${isToday ? "bg-blue-800 text-white" : "text-slate-700"}`}>
                        {d.getDate()}
                      </span>
                      {isOperaio && mineCount > 0 && (
                        <span title="Lavori tuoi" className="text-[10px] font-bold bg-emerald-500 text-white rounded-full px-1.5 py-0.5 inline-flex items-center gap-0.5">
                          <Star className="w-2.5 h-2.5"/> {mineCount}
                        </span>
                      )}
                    </div>
                    <div className="flex-1 mt-1 space-y-0.5 overflow-hidden">
                      {list.slice(0, 3).map(j => {
                        const mine = isMine(j);
                        const c = catColor(j.category);
                        const base = isOperaio && !mine
                          ? "bg-slate-100 text-slate-400 opacity-60"
                          : mine
                            ? "bg-emerald-100 text-emerald-900 font-semibold ring-1 ring-emerald-300"
                            : `${c.bg} ${c.text}`;
                        return (
                          <div key={j.job_id} className={`text-[10px] rounded px-1 py-0.5 truncate flex items-center gap-1 ${base}`}>
                            <span className={`w-1 h-1 rounded-full ${isOperaio && !mine ? "bg-slate-400" : mine ? "bg-emerald-600" : c.dot}`}/>
                            <span className="truncate">{j.title}</span>
                          </div>
                        );
                      })}
                      {list.length > 3 && (
                        <div className="text-[10px] text-slate-500">+ {list.length - 3} altri</div>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          </Card>
        ) : (
          <Card className="p-5">
            <div className="text-xs text-slate-500 uppercase tracking-widest mb-1">
              {new Date(selectedDay).toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
            </div>
            <h3 className="font-bold text-slate-900 text-lg mb-3">Lavori programmati ({dayJobs.length})</h3>
            {dayJobs.length === 0 ? (
              <div className="text-center py-10 text-slate-500">Nessun lavoro programmato in questo giorno.</div>
            ) : (
              <DayList jobs={dayJobs} isMine={isMine} navigate={navigate} canOpen={true}/>
            )}
          </Card>
        )}

        {view === "month" && (
          <Card className="mt-4 p-4">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-bold text-slate-900">
                {selectedDay === toISO(today) ? "Oggi" : parseISO(selectedDay)?.toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long" })}
              </h3>
              <span className="text-sm text-slate-500">{dayJobs.length} {dayJobs.length === 1 ? "lavoro" : "lavori"}</span>
            </div>
            {busy ? <Loader2 className="w-5 h-5 animate-spin text-blue-800 mx-auto my-4"/> :
             dayJobs.length === 0 ? (
              <p className="text-sm text-slate-500 italic">Nessun lavoro programmato per questo giorno.</p>
             ) : (
              <DayList jobs={dayJobs} isMine={isMine} navigate={navigate} canOpen={true}/>
             )}
          </Card>
        )}
      </div>
    </div>
  );
}

function DayList({ jobs, isMine, navigate }) {
  return (
    <div className="space-y-2" data-testid="day-jobs-list">
      {jobs.map(j => {
        const mine = isMine(j);
        const c = catColor(j.category);
        return (
          <button key={j.job_id} onClick={() => navigate(`/lavori/${j.job_id}`)}
                  className={`w-full text-left rounded-lg border p-3 transition flex items-start gap-3 ${
                    mine
                      ? "border-emerald-400 bg-emerald-50 hover:bg-emerald-100 shadow-sm"
                      : (isMine(j) ? "" : "") + " border-slate-200 bg-white hover:bg-slate-50 " + (mine ? "" : "opacity-100")
                  } ${!mine && (window.__isOperaioView || false) ? "opacity-70" : ""}`}
                  data-testid={`day-job-${j.job_id}`}>
            {mine && <Star className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5"/>}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`w-2 h-2 rounded-full ${c.dot}`}/>
                <span className={`font-semibold ${mine ? "text-emerald-900" : "text-slate-900"} truncate`}>{j.title}</span>
                <span className="text-[10px] uppercase tracking-widest bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded font-semibold">{STATUS_LABEL[j.status] || j.status}</span>
                {mine && <span className="text-[10px] uppercase tracking-widest bg-emerald-600 text-white px-1.5 py-0.5 rounded font-bold">⭐ Tuo</span>}
              </div>
              <div className="text-xs text-slate-500 mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
                <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3"/>{j.city}{j.address ? ` · ${j.address}` : ""}</span>
                {j.client_name && <span className="inline-flex items-center gap-1"><User className="w-3 h-3"/>{j.client_name}</span>}
                <span className="inline-flex items-center gap-1"><Briefcase className="w-3 h-3"/>{j.category}</span>
                {(j.assigned_operai_names || []).length > 0 && (
                  <span className="text-slate-400">Operai: {(j.assigned_operai_names || []).join(", ")}</span>
                )}
              </div>
            </div>
          </button>
        );
      })}
    </div>
  );
}
